/**
 * Admin Panel - Reorganized with Tabs
 * "Control Room" header with quick-action sync button
 */
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import AdminPinModal from '../components/AdminPinModal';
import {
  LayoutDashboard, Disc3, ListMusic, Users, Settings, XCircle,
  ArrowLeft, RefreshCw, Funnel
} from 'lucide-react';

// Lazy load tab components for better performance
const AdminDashboard = lazy(() => import('../components/admin/AdminDashboard'));
const AdminTracks = lazy(() => import('../components/admin/AdminTracks'));
const AdminPlaylists = lazy(() => import('../components/admin/AdminPlaylists'));
const AdminUsers = lazy(() => import('../components/admin/AdminUsers'));
const AdminSystem = lazy(() => import('../components/admin/AdminSystem'));
const AdminCancellations = lazy(() => import('../components/admin/AdminCancellations'));
const AdminFunnel = lazy(() => import('../components/admin/AdminFunnel'));

const TABS = [
  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'funnel', label: 'Funnel', Icon: Funnel },
  { id: 'tracks', label: 'Tracks', Icon: Disc3 },
  { id: 'playlists', label: 'Playlists', Icon: ListMusic },
  { id: 'usuarios', label: 'Usuarios', Icon: Users },
  { id: 'cancelaciones', label: 'Cancelaciones', Icon: XCircle },
  { id: 'sistema', label: 'Sistema', Icon: Settings },
];

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20">
    <div className="w-8 h-8 rounded-full border-2 border-[#262626] border-t-[#00D9FF] animate-spin" />
  </div>
);

const AdminPanel = ({ user: initialUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser || null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Admin PIN verification
  const [isAdminVerified, setIsAdminVerified] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        setIsAdminVerified(true);
      }
      checkAdminAccess();
    }
  }, [user]);

  const checkAdminAccess = async () => {
    try {
      const res = await api.get('/admin/stats');
      if (res.status === 200) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Admin access error:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
      setLoading(false);
    }
  };

  const requireAdminPin = (action) => {
    if (isAdminVerified || user?.role === 'admin') {
      const result = action();
      if (result && typeof result.catch === 'function') {
        result.catch(error => console.error('Admin action error:', error));
      }
    } else {
      setPendingAction(() => action);
      setShowPinModal(true);
    }
  };

  const handlePinSuccess = () => {
    setIsAdminVerified(true);
    setShowPinModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleQuickSync = async () => {
    if (syncing) return;
    if (!window.confirm('Sincronizar con Google Drive?\n\nImporta tracks nuevos con BPM/Key del nombre del archivo.')) return;
    setSyncing(true);
    try {
      const res = await api.post('/admin/sync-drive', {}, { timeout: 30000 });
      if (res.data.status === 'already_running') {
        alert('Ya hay una sincronización en progreso.');
      }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTimeout(() => setSyncing(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#262626] border-t-[#00D9FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="bg-[#050505] border-b border-[#1A1A1A] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="h-12 flex items-center justify-between gap-4">

            {/* Left: Logo */}
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <img src="/logo-icon.png" alt="WTPL" className="h-6 w-6" />
              <span className="text-sm font-bold text-white hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>
                WTPL
              </span>
              <span className="text-[9px] font-mono font-bold text-[#00D9FF] tracking-wider hidden md:block">
                ADMIN
              </span>
            </div>

            {/* Center: Tabs */}
            <nav className="flex items-center gap-0.5">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`admin-tab-${tab.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${
                      isActive
                        ? 'bg-[#00D9FF]/10 text-[#00D9FF] border border-[#00D9FF]/20'
                        : 'text-[#52525B] hover:text-white hover:bg-[#141414] border border-transparent'
                    }`}
                  >
                    <tab.Icon className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">{tab.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Right: Quick Sync + Back + Avatar */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Quick Sync Button */}
              <button
                onClick={() => requireAdminPin(handleQuickSync)}
                disabled={syncing}
                data-testid="quick-sync-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00D9FF]/10 border border-[#00D9FF]/20 text-[#00D9FF] rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider hover:bg-[#00D9FF]/15 transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
              </button>

              {/* Back to Dashboard */}
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="admin-back-btn"
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[#52525B] hover:text-white hover:bg-[#141414] rounded-sm transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span className="hidden lg:inline text-[10px] font-mono">Pool</span>
              </button>

              {/* Avatar */}
              <img
                src={user?.picture || '/default-avatar.png'}
                alt={user?.name}
                className="w-7 h-7 rounded-full border border-[#262626]"
                onError={(e) => { e.target.src = '/default-avatar.png'; }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 py-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <Suspense fallback={<LoadingSpinner />}>
              {activeTab === 'dashboard' && (
                <AdminDashboard user={user} />
              )}
              {activeTab === 'funnel' && (
                <AdminFunnel />
              )}
              {activeTab === 'tracks' && (
                <AdminTracks user={user} requireAdminPin={requireAdminPin} />
              )}
              {activeTab === 'playlists' && (
                <AdminPlaylists user={user} requireAdminPin={requireAdminPin} />
              )}
              {activeTab === 'usuarios' && (
                <AdminUsers user={user} requireAdminPin={requireAdminPin} />
              )}
              {activeTab === 'cancelaciones' && (
                <AdminCancellations />
              )}
              {activeTab === 'sistema' && (
                <AdminSystem user={user} requireAdminPin={requireAdminPin} />
              )}
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Admin PIN Modal */}
      <AdminPinModal
        isOpen={showPinModal}
        onClose={() => {
          setShowPinModal(false);
          setPendingAction(null);
        }}
        onSuccess={handlePinSuccess}
      />
    </div>
  );
};

export default AdminPanel;
